import React from "react";
import { motion } from "framer-motion";
import { Target, Eye, Heart, Users } from "lucide-react";
import siteSettings from "../data/site-settings.json";
import { ABOUT } from "../constants/testIds";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};
const stagger = { visible: { transition: { staggerChildren: 0.12 } } };

const AboutPage = () => {
  const team = [
    { name: "Yousuf", role: "Partner", initial: "Y" },
    { name: "Faizal", role: "Partner", initial: "F" },
    { name: "Ashiq", role: "Partner", initial: "A" },
  ];

  const values = [
    { icon: <Heart size={22} className="text-premiumGold" />, title: "Quality First", text: "We never compromise on the quality of our products, ensuring every item meets premium standards." },
    { icon: <Users size={22} className="text-premiumGold" />, title: "Customer Trust", text: "Building long-term relationships with our customers through honest and transparent dealings." },
    { icon: <Target size={22} className="text-premiumGold" />, title: "Consistency", text: "Delivering the same high standards every day, every order, without exception." },
    { icon: <Eye size={22} className="text-premiumGold" />, title: "Transparency", text: "Open and clear communication on pricing, availability, and product sourcing." },
  ];

  return (
    <div className="pt-20" data-testid={ABOUT.pageSection}>
      {/* Hero */}
      <section className="relative bg-deepCharcoal py-28 overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <img
            src={siteSettings.store_image}
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-deepCharcoal/60 via-deepCharcoal/40 to-deepCharcoal/80" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
          >
            <motion.span variants={fadeUp} className="section-label" style={{ color: "#C9A96E" }}>
              Our Story
            </motion.span>
            <motion.h1
              variants={fadeUp}
              className="font-cormorant text-5xl sm:text-6xl font-bold text-white mt-3 mb-4"
            >
              About AL ZAHRAN FLOUR MILL
            </motion.h1>
            <motion.div
              variants={fadeUp}
              className="gold-divider mx-auto mb-6"
            />
            <motion.p
              variants={fadeUp}
              className="font-outfit text-base text-white/70 max-w-2xl mx-auto leading-relaxed"
            >
              A trusted food commodity supplier in Dubai, serving wholesale and retail customers with premium quality products and dependable service.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-24 bg-shadowWhite" data-testid={ABOUT.storySection}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="relative"
            >
              <img
                src={siteSettings.store_interior}
                alt="AL ZAHRAN FLOUR MILL Store Interior"
                className="w-full h-80 object-cover"
              />
              <div className="absolute -bottom-4 -right-4 w-full h-full border-2 border-premiumGold/30 pointer-events-none" />
              {/* Gold frame accent */}
              <div className="absolute top-4 left-4 w-12 h-12 border-t-2 border-l-2 border-premiumGold" />
              <div className="absolute bottom-4 right-4 w-12 h-12 border-b-2 border-r-2 border-premiumGold" />
            </motion.div>

            {/* Text */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              variants={stagger}
              viewport={{ once: true }}
            >
              <motion.span variants={fadeUp} className="section-label">Our Journey</motion.span>
              <motion.h2
                variants={fadeUp}
                className="font-cormorant text-4xl sm:text-5xl font-bold text-deepCharcoal mt-3 mb-4"
              >
                Built on Quality & Trust
              </motion.h2>
              <motion.div variants={fadeUp} className="gold-divider mb-6" />
              <motion.p
                variants={fadeUp}
                className="font-outfit text-base text-[#4A4A4A] leading-relaxed mb-4"
              >
                AL ZAHRAN FLOUR MILL, operating under the HORALANZ brand, is a premium food commodity supplier based in Dubai, UAE. We specialize in wholesale and retail supply of essential food products — from flour and rice to oils, spices, nuts, and daily grocery items.
              </motion.p>
              <motion.p
                variants={fadeUp}
                className="font-outfit text-base text-[#4A4A4A] leading-relaxed mb-6"
              >
                Our business is built on three core principles: quality sourcing, consistent availability, and customer trust. We maintain strong relationships with reputable suppliers to ensure our customers always receive the best products at competitive prices.
              </motion.p>
              <motion.div variants={fadeUp} className="grid grid-cols-2 gap-4">
                <div className="bg-softIvory border border-goldBorder p-4">
                  <p className="font-cormorant text-3xl font-bold text-premiumGold">6+</p>
                  <p className="font-outfit text-xs text-[#4A4A4A] uppercase tracking-wide mt-1">Product Categories</p>
                </div>
                <div className="bg-softIvory border border-goldBorder p-4">
                  <p className="font-cormorant text-3xl font-bold text-premiumGold">Daily</p>
                  <p className="font-outfit text-xs text-[#4A4A4A] uppercase tracking-wide mt-1">Operations</p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-softIvory">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div
              initial="hidden"
              whileInView="visible"
              variants={stagger}
              viewport={{ once: true }}
              className="bg-white border border-goldBorder p-10 relative overflow-hidden"
            >
              <span className="absolute top-0 left-0 w-1 h-full bg-premiumGold" />
              <motion.div variants={fadeUp}>
                <Target size={32} className="text-premiumGold mb-4" />
                <h3 className="font-cormorant text-3xl font-bold text-deepCharcoal mb-3">Our Mission</h3>
                <div className="gold-divider mb-5" />
                <p className="font-outfit text-base text-[#4A4A4A] leading-relaxed">
                  To provide quality food commodities with dependable service and competitive pricing, empowering our customers — from home cooks to large-scale businesses — with the best products the market has to offer.
                </p>
              </motion.div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              variants={stagger}
              viewport={{ once: true }}
              className="bg-deepCharcoal border border-premiumGold/30 p-10 relative overflow-hidden"
            >
              <span className="absolute top-0 left-0 w-1 h-full bg-premiumGold" />
              <motion.div variants={fadeUp}>
                <Eye size={32} className="text-premiumGold mb-4" />
                <h3 className="font-cormorant text-3xl font-bold text-white mb-3">Our Vision</h3>
                <div className="gold-divider mb-5" />
                <p className="font-outfit text-base text-white/70 leading-relaxed">
                  To become a leading and trusted food commodity supplier in the region, recognized for our unwavering commitment to quality, innovation in service delivery, and lasting partnerships with our valued customers.
                </p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-shadowWhite">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-14"
            initial="hidden"
            whileInView="visible"
            variants={stagger}
            viewport={{ once: true }}
          >
            <motion.span variants={fadeUp} className="section-label">What Drives Us</motion.span>
            <motion.h2
              variants={fadeUp}
              className="font-cormorant text-4xl sm:text-5xl font-bold text-deepCharcoal mt-3 mb-4"
            >
              Our Core Values
            </motion.h2>
            <motion.div variants={fadeUp} className="gold-divider mx-auto" />
          </motion.div>

          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {values.map((v, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                className="bg-white border border-goldBorder p-7 hover:-translate-y-1 transition-transform duration-300"
              >
                <div className="mb-4">{v.icon}</div>
                <h3 className="font-cormorant text-xl font-semibold text-deepCharcoal mb-2">{v.title}</h3>
                <p className="font-outfit text-sm text-[#4A4A4A] leading-relaxed">{v.text}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Leadership Team */}
      <section className="py-20 bg-softIvory" data-testid={ABOUT.teamSection}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-14"
            initial="hidden"
            whileInView="visible"
            variants={stagger}
            viewport={{ once: true }}
          >
            <motion.span variants={fadeUp} className="section-label">Leadership</motion.span>
            <motion.h2
              variants={fadeUp}
              className="font-cormorant text-4xl sm:text-5xl font-bold text-deepCharcoal mt-3 mb-4"
            >
              Our Partners
            </motion.h2>
            <motion.div variants={fadeUp} className="gold-divider mx-auto" />
          </motion.div>

          <motion.div
            className="grid grid-cols-1 sm:grid-cols-3 gap-8 max-w-2xl mx-auto"
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {team.map((member, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                className="text-center"
              >
                <div className="w-24 h-24 bg-deepCharcoal border-2 border-premiumGold rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="font-cormorant text-3xl font-bold text-premiumGold">
                    {member.initial}
                  </span>
                </div>
                <h3 className="font-cormorant text-2xl font-bold text-deepCharcoal">{member.name}</h3>
                <p className="section-label mt-1">{member.role}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Store Image */}
      <section className="relative h-64 overflow-hidden">
        <img
          src={siteSettings.store_image}
          alt="AL ZAHRAN FLOUR MILL"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-deepCharcoal/60 flex items-center justify-center">
          <div className="text-center">
            <p className="font-cormorant text-3xl sm:text-4xl font-bold text-white mb-2">
              Your Trusted Food Supplier in Dubai
            </p>
            <p className="font-outfit text-sm text-premiumGold tracking-widest uppercase">
              {siteSettings.hours}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
